import React, { useState, useEffect, useCallback } from "react";
import {
  ButtonGroup,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Tooltip,
} from "@mui/material";
import {
  IoAdd,
  IoBusinessOutline,
  IoChevronDown,
  IoChevronUp,
  IoPencil,
  IoRefresh,
} from "react-icons/io5";
import { IoLockClosed, IoEyeOff, IoCheckmarkCircle } from "react-icons/io5";

import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import VuiButton from "components/ui/VuiButton";
import VuiInput from "components/ui/VuiInput";
import VuiBadge from "components/ui/VuiBadge";
import VuiAlert from "components/ui/VuiAlert";

import { adminService } from "services/adminService";
import { useSnackbar } from "notistack";

// ─── Constants ───────────────────────────────────────────────────────────────

const ALL_PAGES = [
  { key: "press-digest-v2",     label: "Press Digest" },
  { key: "digital-presence-v2", label: "LinkedIn Watch" },
  { key: "content-strategy",    label: "Content Strategy" },
  { key: "product-trends",      label: "Product Trends" },
  { key: "expansion-trends",    label: "Expansion Trends" },
  { key: "product-showdown",    label: "Product Showdown" },
  { key: "pricing-analytics",   label: "Pricing Analytics" },
  { key: "rd-landscape",        label: "R&D Landscape" },
  { key: "competitor-reports",  label: "Competitor Reports" },
  { key: "kol-insights",        label: "KOL Insights" },
];

const INDUSTRIES = ["MEDTECH", "PHARMA", "BIOTECH"];

// Page states
const PAGE_HIDDEN  = "hidden";
const PAGE_LOCKED  = "locked";
const PAGE_ENABLED = "enabled";

/**
 * Derive a per-page state map from enabledPages + lockedPages arrays.
 * Keys not in either list default to hidden.
 */
function buildPageStateMap(enabledPages = [], lockedPages = []) {
  const map = {};
  ALL_PAGES.forEach(({ key }) => {
    if (enabledPages.includes(key)) map[key] = PAGE_ENABLED;
    else if (lockedPages.includes(key)) map[key] = PAGE_LOCKED;
    else map[key] = PAGE_HIDDEN;
  });
  return map;
}

/**
 * Convert a page state map back to { enabledPages, lockedPages } arrays.
 */
function pageStateMapToLists(stateMap) {
  const enabledPages = [];
  const lockedPages  = [];
  ALL_PAGES.forEach(({ key }) => {
    if (stateMap[key] === PAGE_ENABLED) enabledPages.push(key);
    else if (stateMap[key] === PAGE_LOCKED) lockedPages.push(key);
  });
  return { enabledPages, lockedPages };
}

const DEFAULT_PAGE_STATE = () => {
  const map = {};
  ALL_PAGES.forEach(({ key }) => { map[key] = PAGE_ENABLED; });
  return map;
};

const DEFAULT_FORM = {
  clientCompanyId: "",
  name: "",
  industry: "MEDTECH",
  pageState: DEFAULT_PAGE_STATE(),
};

// ─── PageStateSelector ────────────────────────────────────────────────────────
// Segmented Hidden | Locked | Enabled button for a single page row.

const STATE_CONFIG = {
  [PAGE_HIDDEN]:  { label: "Hidden",  icon: <IoEyeOff size={12} />,          color: "#7B809A" },
  [PAGE_LOCKED]:  { label: "Locked",  icon: <IoLockClosed size={12} />,      color: "#fb8c00" },
  [PAGE_ENABLED]: { label: "Enabled", icon: <IoCheckmarkCircle size={12} />, color: "#17c1e8" },
};

function PageStateSelector({ pageKey, label, value, onChange }) {
  return (
    <VuiBox
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      px={1.5}
      py={0.75}
      sx={{
        borderRadius: "8px",
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <VuiTypography variant="caption" sx={{ minWidth: 130, color: "rgba(255,255,255,0.75) !important" }}>
        {label}
      </VuiTypography>
      <ButtonGroup size="small" disableElevation>
        {[PAGE_HIDDEN, PAGE_LOCKED, PAGE_ENABLED].map(state => {
          const { label: stateLabel, icon, color } = STATE_CONFIG[state];
          const active = value === state;
          return (
            <Button
              key={state}
              onClick={() => onChange(pageKey, state)}
              startIcon={icon}
              sx={{
                fontSize: "11px",
                textTransform: "none",
                px: 1,
                py: 0.25,
                borderColor: "rgba(255,255,255,0.15) !important",
                color: active ? color : "rgba(255,255,255,0.3)",
                background: active ? `${color}22` : "transparent",
                fontWeight: active ? "bold" : "normal",
                "&:hover": { background: `${color}18`, color: color },
              }}
            >
              {stateLabel}
            </Button>
          );
        })}
      </ButtonGroup>
    </VuiBox>
  );
}

// ─── Page summary chips (read-only view) ─────────────────────────────────────

function PageSummary({ enabledPages = [], lockedPages = [] }) {
  const hidden = ALL_PAGES.filter(p =>
    !enabledPages.includes(p.key) && !lockedPages.includes(p.key)
  );

  const badgeSx = (borderColor, textColor) => ({
    "& .MuiBadge-badge": {
      border: `1px solid ${borderColor}`,
      color: textColor,
      background: `${borderColor}18`,
    },
  });

  return (
    <VuiBox display="flex" flexWrap="wrap" gap={0.5}>
      {enabledPages.map(key => {
        const page = ALL_PAGES.find(p => p.key === key);
        return (
          <VuiBadge key={key} variant="standard" badgeContent={page?.label || key} size="xs" container
            sx={badgeSx("#17c1e8", "#fff")} />
        );
      })}
      {lockedPages.map(key => {
        const page = ALL_PAGES.find(p => p.key === key);
        return (
          <VuiBadge key={key} variant="standard" badgeContent={`🔒 ${page?.label || key}`} size="xs" container
            sx={badgeSx("#fb8c00", "#fff")} />
        );
      })}
      {hidden.length > 0 && (
        <VuiTypography variant="caption" color="text" sx={{ opacity: 0.4, alignSelf: "center" }}>
          +{hidden.length} hidden
        </VuiTypography>
      )}
    </VuiBox>
  );
}

// ─── ClientDetailRow ──────────────────────────────────────────────────────────

function ClientDetailRow({ client, clientOrgs, onUpdated }) {
  const { enqueueSnackbar } = useSnackbar();
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: client.name,
    industry: client.industry,
    pageState: buildPageStateMap(client.enabled_pages, client.locked_pages),
    active: client.active,
  });

  const orgName = clientOrgs.find(o => o.id === client.client_company_id)?.name || client.name;

  const handlePageChange = (key, state) => {
    setForm(f => ({ ...f, pageState: { ...f.pageState, [key]: state } }));
  };

  const handleSave = async () => {
    setSaving(true);
    const { enabledPages, lockedPages } = pageStateMapToLists(form.pageState);
    try {
      await adminService.updateClient(client.id, {
        name: form.name,
        industry: form.industry,
        enabled_pages: enabledPages,
        locked_pages: lockedPages,
        active: form.active,
      });
      enqueueSnackbar("Client updated", { variant: "success" });
      setEditing(false);
      onUpdated();
    } catch {
      enqueueSnackbar("Failed to update client", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Summary row */}
      <VuiBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={1.5}
        sx={{
          borderRadius: "10px",
          mb: 1,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.07)",
          cursor: "pointer",
          "&:hover": { background: "rgba(255,255,255,0.05)" },
        }}
        onClick={() => setExpanded(e => !e)}
      >
        <VuiBox display="flex" alignItems="center" gap={2}>
          <IoBusinessOutline size={18} color="#1AB6FF" />
          <VuiBox>
            <VuiTypography variant="button" color="white" fontWeight="medium">
              {orgName}
            </VuiTypography>
            <VuiTypography variant="caption" color="text" sx={{ display: "block" }}>
              {client.industry} · {(client.enabled_pages || []).length} enabled
              {(client.locked_pages || []).length > 0 && ` · ${client.locked_pages.length} locked`}
            </VuiTypography>
          </VuiBox>
        </VuiBox>

        <VuiBox display="flex" alignItems="center" gap={1}>
          <VuiBadge
            variant="standard"
            badgeContent={client.active ? "Active" : "Inactive"}
            size="xs"
            container
            sx={({ palette: { success, error, white } }) => ({
              border: `1px solid ${client.active ? success.main : error.main}`,
              color: white.main,
            })}
          />
          {expanded ? <IoChevronUp color="white" /> : <IoChevronDown color="white" />}
        </VuiBox>
      </VuiBox>

      {/* Expanded detail */}
      <Collapse in={expanded}>
        <VuiBox
          px={3}
          py={2}
          mb={2}
          sx={{
            borderRadius: "10px",
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          {!editing ? (
            <>
              <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <VuiBox>
                  <VuiTypography variant="caption" color="text">Company ID</VuiTypography>
                  <VuiTypography variant="caption" color="white"
                    sx={{ display: "block", fontFamily: "monospace", fontSize: "11px" }}>
                    {client.client_company_id}
                  </VuiTypography>
                </VuiBox>
                <VuiButton variant="outlined" color="info" size="small"
                  onClick={e => { e.stopPropagation(); setEditing(true); }}
                  startIcon={<IoPencil />}
                >
                  Edit
                </VuiButton>
              </VuiBox>

              <VuiTypography variant="caption" color="text" fontWeight="bold"
                sx={{ mb: 1, display: "block" }}>
                Pages
              </VuiTypography>
              <PageSummary
                enabledPages={client.enabled_pages}
                lockedPages={client.locked_pages}
              />
            </>
          ) : (
            <VuiBox onClick={e => e.stopPropagation()}>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} md={6}>
                  <VuiTypography variant="caption" color="white" fontWeight="bold">Name</VuiTypography>
                  <VuiInput
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <VuiTypography variant="caption" color="white" fontWeight="bold">Industry</VuiTypography>
                  <VuiBox component="select" value={form.industry}
                    onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}
                    sx={{
                      mt: 0.5, width: "100%", background: "#0f1535", color: "white",
                      border: "1px solid #7B809A", borderRadius: "8px", padding: "8px 12px",
                    }}
                  >
                    {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                  </VuiBox>
                </Grid>
              </Grid>

              <VuiTypography variant="caption" color="white" fontWeight="bold"
                sx={{ display: "block", mb: 1 }}>
                Page Access
              </VuiTypography>
              <VuiTypography variant="caption" color="text" sx={{ display: "block", mb: 1.5 }}>
                <strong style={{ color: "#17c1e8" }}>Enabled</strong> — visible + accessible ·{" "}
                <strong style={{ color: "#fb8c00" }}>Locked</strong> — visible with lock icon, not accessible ·{" "}
                <strong style={{ color: "#7B809A" }}>Hidden</strong> — not shown
              </VuiTypography>
              <VuiBox display="flex" flexDirection="column" gap={0.75} mb={2}>
                {ALL_PAGES.map(page => (
                  <PageStateSelector
                    key={page.key}
                    pageKey={page.key}
                    label={page.label}
                    value={form.pageState[page.key]}
                    onChange={handlePageChange}
                  />
                ))}
              </VuiBox>

              <FormControlLabel
                control={
                  <Switch checked={form.active}
                    onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                    color="success" />
                }
                label={<VuiTypography variant="caption" color="text">Active</VuiTypography>}
              />

              <VuiBox display="flex" gap={1} mt={2}>
                <VuiButton color="info" size="small" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </VuiButton>
                <VuiButton variant="outlined" color="secondary" size="small"
                  onClick={() => setEditing(false)}>
                  Cancel
                </VuiButton>
              </VuiBox>
            </VuiBox>
          )}
        </VuiBox>
      </Collapse>
    </>
  );
}

// ─── New Client Dialog ────────────────────────────────────────────────────────

function NewClientDialog({ open, onClose, clientOrgs, onCreated }) {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setForm({ ...DEFAULT_FORM, pageState: DEFAULT_PAGE_STATE() }); setError(""); }
  }, [open]);

  const handlePageChange = (key, state) => {
    setForm(f => ({ ...f, pageState: { ...f.pageState, [key]: state } }));
  };

  const handleSubmit = async () => {
    if (!form.clientCompanyId || !form.name) {
      setError("Client company and name are required.");
      return;
    }
    setSaving(true);
    setError("");
    const { enabledPages, lockedPages } = pageStateMapToLists(form.pageState);
    try {
      await adminService.createClient({
        client_company_id: form.clientCompanyId,
        name: form.name,
        industry: form.industry,
        enabled_pages: enabledPages,
        locked_pages: lockedPages,
      });
      enqueueSnackbar("Client created successfully", { variant: "success" });
      onCreated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { background: "#0f1535", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "16px" },
      }}
    >
      <DialogTitle sx={{ color: "white", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <VuiTypography variant="h6" color="white">New Client</VuiTypography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && <VuiBox mb={2}><VuiAlert color="error">{error}</VuiAlert></VuiBox>}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <VuiTypography variant="caption" color="white" fontWeight="bold">Client Name</VuiTypography>
            <VuiInput
              placeholder="e.g. Terumo Neuro"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              sx={{ mt: 0.5 }}
            />
          </Grid>

          <Grid item xs={12}>
            <VuiTypography variant="caption" color="white" fontWeight="bold">
              Company Entity
            </VuiTypography>
            <VuiTypography variant="caption" color="text" sx={{ display: "block", mb: 0.5 }}>
              The company record this client maps to. Must have competitors configured in MongoDB.
            </VuiTypography>
            <VuiBox
              component="select"
              value={form.clientCompanyId}
              onChange={e => {
                const org = clientOrgs.find(o => o.id === e.target.value);
                setForm(f => ({ ...f, clientCompanyId: e.target.value, name: f.name || org?.name || "" }));
              }}
              sx={{
                width: "100%", background: "#0f1535", color: "white",
                border: "1px solid #7B809A", borderRadius: "8px", padding: "8px 12px",
              }}
            >
              <option value="">— Select a company —</option>
              {clientOrgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </VuiBox>
          </Grid>

          <Grid item xs={12} md={6}>
            <VuiTypography variant="caption" color="white" fontWeight="bold">Industry</VuiTypography>
            <VuiBox component="select" value={form.industry}
              onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}
              sx={{
                mt: 0.5, width: "100%", background: "#0f1535", color: "white",
                border: "1px solid #7B809A", borderRadius: "8px", padding: "8px 12px",
              }}
            >
              {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
            </VuiBox>
          </Grid>

          <Grid item xs={12}>
            <VuiTypography variant="caption" color="white" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
              Page Access
            </VuiTypography>
            <VuiTypography variant="caption" color="text" sx={{ display: "block", mb: 1 }}>
              <strong style={{ color: "#17c1e8" }}>Enabled</strong> — accessible ·{" "}
              <strong style={{ color: "#fb8c00" }}>Locked</strong> — visible, upsell prompt ·{" "}
              <strong style={{ color: "#7B809A" }}>Hidden</strong> — not shown
            </VuiTypography>
            <VuiBox display="flex" flexDirection="column" gap={0.75}>
              {ALL_PAGES.map(page => (
                <PageStateSelector
                  key={page.key}
                  pageKey={page.key}
                  label={page.label}
                  value={form.pageState[page.key]}
                  onChange={handlePageChange}
                />
              ))}
            </VuiBox>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <VuiButton variant="outlined" color="secondary" onClick={onClose}>Cancel</VuiButton>
        <VuiButton color="info" onClick={handleSubmit} disabled={saving}>
          {saving ? "Creating…" : "Create Client"}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function ClientManagement() {
  const [clients, setClients] = useState([]);
  const [clientOrgs, setClientOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newClientOpen, setNewClientOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [clientsRes, orgsRes] = await Promise.all([
        adminService.getClients(),
        adminService.getClientOrgs(),
      ]);
      setClients(Array.isArray(clientsRes) ? clientsRes : clientsRes.data || []);
      setClientOrgs(Array.isArray(orgsRes) ? orgsRes : orgsRes.data || []);
    } catch {
      enqueueSnackbar("Failed to load client data", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <VuiBox>
      <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <VuiBox>
          <VuiTypography variant="lg" color="white" fontWeight="bold">
            Client Organisations
          </VuiTypography>
          <VuiTypography variant="caption" color="text">
            {clients.length} active client{clients.length !== 1 ? "s" : ""}
          </VuiTypography>
        </VuiBox>
        <VuiBox display="flex" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadData} sx={{ color: "white" }}>
              <IoRefresh size={18} />
            </IconButton>
          </Tooltip>
          <VuiButton color="info" size="small" startIcon={<IoAdd />}
            onClick={() => setNewClientOpen(true)}>
            New Client
          </VuiButton>
        </VuiBox>
      </VuiBox>

      {loading ? (
        <VuiBox display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} sx={{ color: "#1AB6FF" }} />
        </VuiBox>
      ) : clients.length === 0 ? (
        <VuiBox py={6} display="flex" flexDirection="column" alignItems="center" sx={{ opacity: 0.5 }}>
          <IoBusinessOutline size={32} color="white" />
          <VuiTypography variant="body2" color="text" mt={1}>No clients configured yet.</VuiTypography>
          <VuiButton color="info" size="small" sx={{ mt: 2 }} onClick={() => setNewClientOpen(true)}>
            Create First Client
          </VuiButton>
        </VuiBox>
      ) : (
        clients.map(client => (
          <ClientDetailRow
            key={client.id}
            client={client}
            clientOrgs={clientOrgs}
            onUpdated={loadData}
          />
        ))
      )}

      <NewClientDialog
        open={newClientOpen}
        onClose={() => setNewClientOpen(false)}
        clientOrgs={clientOrgs}
        onCreated={loadData}
      />
    </VuiBox>
  );
}

export default ClientManagement;
